import React, { useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Lock, ShieldCheck } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import {
  useSessionDetail, useSessionGrid, STATUS_COLORS, StatusBadge,
} from './config';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import LaneColumn from './components/LaneColumn';
import MoveModal from './components/MoveModal';
import CloseSessionDialog from './components/CloseSessionDialog';
import Tabs from '../../components/ui/Tabs';
import WorkspaceListTab from './workspace/WorkspaceListTab';
import WorkspaceDistributionTab from './workspace/WorkspaceDistributionTab';
import WorkspaceSummaryTab from './workspace/WorkspaceSummaryTab';
import {
  TAB_DISTRIBUTION, TAB_LIST, TAB_MATRIX, TAB_SUMMARY, WORKSPACE_TABS,
} from './workspace/config';

const Container = styled.div``;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SessionName = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ModeratorBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  background: rgba(2, 145, 239, 0.08);
  color: #0291EF;
  font-size: 11px;
  font-weight: 700;
`;

const ToleranceLabel = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #64748B;
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LanesRow = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 8px;
`;

const EmptyHint = styled.div`
  padding: 40px;
  text-align: center;
  color: #A0A8B4;
  font-size: 13px;
`;

// Faz B (spec 2026-08-07 §4/§6, Task 5) — kalibrasyon ızgarası: seviye
// kolonları + Önerisiz kolonu + kolon başı dağılım bandı + entry kartları +
// taşıma modalı (yalnız moderatör) + kapanış diyaloğu + 15sn polling.
//
// Moderatör tespiti FE'de: GetById (session DETAYI) üye listesinden — GRID
// UCU üye listesi TAŞIMAZ (SessionGridSessionDto'da Members yok, bkz.
// Dtos/Performance/CalibrationSessionDtos.cs). BE zaten reddeder (notModerator)
// — bu yalnız buton görünürlüğü/UX.
export default function SessionGridPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth();

  const { session, fetching: sessionFetching } = useSessionDetail(id);
  const {
    grid, fetching: gridFetching, refresh, moveEntry, closeSession,
  } = useSessionGrid(id);

  const [moveTarget, setMoveTarget] = useState(null);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  // PM analitik P5 (analiz E08) — çalışma alanı sekmeleri: Matris (mevcut ızgara, varsayılan), Liste, Dağılım, Oturum Özeti.
  // Liste süzgeçleri Özet'teki "eksik gerekçe / görüşülecek" bağlantılarından da ayarlanır.
  const [activeTab, setActiveTab] = useState(TAB_MATRIX);
  const [rowFilters, setRowFilters] = useState({});
  const openList = useCallback((filter) => { setRowFilters(filter || {}); setActiveTab(TAB_LIST); }, []);

  const isModerator = useMemo(() => {
    if (!session?.members || !user?.id) return false;
    return session.members.some((m) => m.userId === user.id && m.role === 'moderator');
  }, [session, user]);

  const sessionActive = grid?.session?.status === 'active';
  const canMove = isModerator && sessionActive;

  const handleMoveSubmit = useCallback(async (req) => moveEntry(req), [moveEntry]);
  const handleCloseSubmit = useCallback(async (justification) => closeSession(justification), [closeSession]);

  if (sessionFetching || gridFetching) {
    return <Container><EmptyHint>{t('common.loading')}</EmptyHint></Container>;
  }

  if (!session || !grid) {
    return (
      <Container>
        <Breadcrumbs items={[
          { label: t('performance.calibrationSession.title'), path: '/performance/calibration-sessions' },
          { label: t('performance.calibrationSession.notFound') },
        ]}
        />
        <EmptyHint>{t('performance.calibrationSession.notFound')}</EmptyHint>
      </Container>
    );
  }

  const statusCfg = STATUS_COLORS[grid.session.status] || STATUS_COLORS.draft;

  return (
    <Container>
      <Breadcrumbs items={[
        { label: t('performance.calibrationSession.title'), path: '/performance/calibration-sessions' },
        { label: grid.session.name },
      ]}
      />

      <HeaderRow>
        <TitleGroup>
          <SessionName>{grid.session.name}</SessionName>
          <StatusBadge data-role="session-status-badge" color={statusCfg.color} bg={statusCfg.bg}>
            {t(`performance.calibrationSession.status.${grid.session.status}`, { defaultValue: grid.session.status })}
          </StatusBadge>
          {isModerator && (
            <ModeratorBadge data-role="moderator-badge">
              <ShieldCheck size={12} />
              {t('performance.calibrationSession.grid.youAreModerator')}
            </ModeratorBadge>
          )}
          <ToleranceLabel data-role="tolerance-label">
            {t('performance.calibrationSession.grid.toleranceLabel')}: ±{grid.session.tolerancePct}%
          </ToleranceLabel>
        </TitleGroup>
        <ActionsRow>
          <Button data-role="manual-refresh-btn" variant="outline" onClick={refresh}>
            <RefreshCw size={14} />
            {t('performance.calibrationSession.grid.refresh')}
          </Button>
          {isModerator && sessionActive && (
            <Button data-role="open-close-dialog-btn" onClick={() => setCloseDialogOpen(true)}>
              <Lock size={14} />
              {t('performance.calibrationSession.closeDialog.confirmButton')}
            </Button>
          )}
        </ActionsRow>
      </HeaderRow>

      <Tabs
        tabs={WORKSPACE_TABS.map((key) => ({ key, label: t(`performance.calibrationWorkspace.tab.${key}`) }))}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {activeTab === TAB_LIST ? (
        <WorkspaceListTab
          sessionId={id} levels={grid.lanes} sessionStatus={grid.session.status}
          rowFilters={rowFilters} onRowFilters={setRowFilters} refreshKey={grid.lastChangedAt}
        />
      ) : null}
      {activeTab === TAB_DISTRIBUTION ? <WorkspaceDistributionTab sessionId={id} refreshKey={grid.lastChangedAt} /> : null}
      {activeTab === TAB_SUMMARY ? <WorkspaceSummaryTab sessionId={id} refreshKey={grid.lastChangedAt} onOpenList={openList} /> : null}

      {activeTab === TAB_MATRIX ? (
        <LanesRow>
          {grid.lanes.map((lane) => (
            <LaneColumn
              key={lane.levelId}
              label={lane.label}
              targetPct={lane.targetPct}
              actualPct={lane.actualPct}
              actualCount={lane.actualCount}
              withinTolerance={lane.withinTolerance}
              entries={lane.entries}
              canMove={canMove}
              onMoveClick={setMoveTarget}
            />
          ))}
          <LaneColumn
            label={t('performance.calibrationSession.grid.unproposedColumn')}
            targetPct={null}
            actualPct={grid.unproposed.actualPct}
            actualCount={grid.unproposed.actualCount}
            withinTolerance
            entries={grid.unproposed.entries}
            canMove={canMove}
            onMoveClick={setMoveTarget}
          />
        </LanesRow>
      ) : null}

      {moveTarget && (
        <MoveModal
          // Analitik Plan 5 — modal ızgaranın GÜNCEL girdisini taşır: 409 sonrası ızgara
          // yenilenince ikinci kaydetme yeni sürümü gönderir (anlık görüntüyle döngü olmaz).
          entry={[...grid.lanes.flatMap((l) => l.entries), ...grid.unproposed.entries]
            .find((e) => e.calibrationRecordId === moveTarget.calibrationRecordId) || moveTarget}
          levels={grid.lanes}
          onClose={() => setMoveTarget(null)}
          onSubmit={handleMoveSubmit}
        />
      )}

      {closeDialogOpen && (
        <CloseSessionDialog
          grid={grid}
          onClose={() => setCloseDialogOpen(false)}
          onSubmit={handleCloseSubmit}
        />
      )}
    </Container>
  );
}
